import { useState } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalendarDays, Eye, MessageCircle, Search, TrendingUp, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import AdminLineChartTooltip from "@/components/admin/AdminLineChartTooltip";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminEmptyState, AdminMetricCard, AdminPanel } from "@/components/admin/AdminSurface";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getTimeRangeAnalytics } from "@/services/analyticsQueriesService";

const COLORS = ["#25D366", "#4b5563", "#16a34a", "#0891b2", "#f59e0b", "#ef4444"];

const formatNumber = (value: number) => new Intl.NumberFormat("en-US").format(value);

export default function AdminAnalyticsPage() {
  const [timeRange, setTimeRange] = useState("30");

  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ["admin-analytics", timeRange],
    queryFn: () => getTimeRangeAnalytics(Number.parseInt(timeRange, 10)),
  });

  if (isLoading) {
    return <AdminLayout title="Analytics" loading />;
  }

  return (
    <AdminLayout
      title="Analytics"
      description="Traffic, searches, and engagement in one place."
      actions={
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="h-10 w-[180px] rounded-lg border-gray-200 bg-white">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <AdminMetricCard
            label="Impressions"
            value={formatNumber(analyticsData?.totalImpressions || 0)}
            description="Provider card impressions"
            icon={Users}
            tone="neutral"
          />
          <AdminMetricCard
            label="Views"
            value={formatNumber(analyticsData?.totalViews || 0)}
            description="Provider profile views"
            icon={Eye}
            tone="accent"
          />
          <AdminMetricCard
            label="Click-through rate"
            value={`${analyticsData?.clickThroughRate || "0.00"}%`}
            description="Impressions converted to views"
            icon={TrendingUp}
            tone="success"
          />
          <AdminMetricCard
            label="Searches"
            value={formatNumber(analyticsData?.totalSearches || 0)}
            description="Search queries performed"
            icon={Search}
            tone="neutral"
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
          <AdminPanel
            title="Traffic trend"
            description={`Daily views and searches across the last ${timeRange} days.`}
            contentClassName="p-0"
          >
            {(analyticsData?.viewsByDay?.length || 0) > 0 ? (
              <div className="px-3 pb-3 pt-4 sm:px-5 sm:pb-5">
                <div className="h-[340px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={(analyticsData?.viewsByDay || []).map((item, index) => ({
                        date: item.date,
                        views: item.views,
                        searches: analyticsData?.searchesByDay?.[index]?.searches || 0,
                      }))}
                      margin={{ top: 12, right: 12, left: -16, bottom: 0 }}
                    >
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                      <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                      <Tooltip content={<AdminLineChartTooltip />} />
                      <Line type="monotone" dataKey="views" stroke="#25D366" strokeWidth={2.5} dot={false} />
                      <Line type="monotone" dataKey="searches" stroke="#0f172a" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="p-5">
                <AdminEmptyState
                  icon={TrendingUp}
                  title="No traffic data yet"
                  description="Traffic charts will appear once enough analytics events are available for the selected time range."
                />
              </div>
            )}
          </AdminPanel>

          <AdminPanel
            title="Engagement mix"
            description="Quick read on contact and session activity."
          >
            <div className="space-y-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
                <p className="text-xs text-gray-500">WhatsApp contacts</p>
                <p className="mt-1 text-xl font-semibold text-gray-800">{formatNumber(analyticsData?.whatsappContacts || 0)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
                <p className="text-xs text-gray-500">Other contacts</p>
                <p className="mt-1 text-xl font-semibold text-gray-800">{formatNumber(analyticsData?.otherContacts || 0)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
                <p className="text-xs text-gray-500">User sessions</p>
                <p className="mt-1 text-xl font-semibold text-gray-800">{formatNumber(analyticsData?.totalSessions || 0)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
                <p className="text-xs text-gray-500">Avg pages/session</p>
                <p className="mt-1 text-xl font-semibold text-gray-800">
                  {analyticsData?.sessionMetrics?.avgPagesVisited || 0}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
                <p className="text-xs text-gray-500">Avg session duration</p>
                <p className="mt-1 text-xl font-semibold text-gray-800">
                  {analyticsData?.sessionMetrics?.avgSessionDuration || 0}m
                </p>
              </div>
            </div>
          </AdminPanel>
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <AdminPanel title="Contact methods" description="Which contact channels are used most often.">
            {(analyticsData?.contactsByType?.length || 0) > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData?.contactsByType || []}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="count"
                      label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {(analyticsData?.contactsByType || []).map((entry, index) => (
                        <Cell key={entry.type} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <AdminEmptyState
                icon={MessageCircle}
                title="No contact data yet"
                description="Contact distribution will appear once users start reaching out through provider contact actions."
              />
            )}
          </AdminPanel>

          <AdminPanel title="Top search terms" description="The strongest search demand for the selected period.">
            {(analyticsData?.topSearchTerms?.length || 0) > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.topSearchTerms || []} layout="vertical" margin={{ left: 12, right: 12 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                    <YAxis
                      dataKey="term"
                      type="category"
                      width={110}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#475569", fontSize: 12 }}
                    />
                    <Tooltip formatter={(value) => [`${value} searches`, "Count"]} />
                    <Bar dataKey="count" fill="#25D366" radius={[8, 8, 8, 8]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <AdminEmptyState
                icon={Search}
                title="No search demand yet"
                description="Popular search terms will appear here as soon as search analytics data is available."
              />
            )}
          </AdminPanel>
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <AdminPanel title="Top providers" description="Providers with the strongest combined views and contacts.">
            {(analyticsData?.topProviders?.length || 0) > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.topProviders || []} layout="vertical" margin={{ left: 12, right: 12 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={120}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#475569", fontSize: 12 }}
                    />
                    <Tooltip formatter={(value) => [`${value} total engagement`, "Engagement"]} />
                    <Bar dataKey="totalEngagement" fill="#4b5563" radius={[8, 8, 8, 8]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <AdminEmptyState
                icon={Eye}
                title="No provider ranking yet"
                description="Top provider rankings will appear once enough engagement data has been recorded."
              />
            )}
          </AdminPanel>

          <AdminPanel title="Popular categories" description="Categories with the strongest total engagement.">
            {(analyticsData?.popularCategories?.length || 0) > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.popularCategories || []}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="category" tickLine={false} axisLine={false} tick={{ fill: "#475569", fontSize: 12 }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                    <Tooltip formatter={(value) => [`${value} total engagement`, "Engagement"]} />
                    <Bar dataKey="totalEngagement" fill="#25D366" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <AdminEmptyState
                icon={CalendarDays}
                title="No category trend yet"
                description="Category engagement will appear when the selected time range has enough activity data."
              />
            )}
          </AdminPanel>
        </div>
      </div>
    </AdminLayout>
  );
}
